import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import axiosInstance from "../api/axiosInstance";

export default function DashboardPage() {
  const { profile, user } = useContext(AuthContext);
  const [refreshedProfile, setRefreshedProfile] = useState(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [permitCapInfo, setPermitCapInfo] = useState(null);

  // Fetch fresh profile data when dashboard loads
  useEffect(() => {
    const refreshProfile = async () => {
      if (!user) return;
      
      setPageLoading(true);
      try {
        // Fetch both profile and permit cap info in parallel
        const [profileResponse, permitCapResponse] = await Promise.all([
          axiosInstance.get('/api/v1/auth/profile/'),
          axiosInstance.get('/api/v1/permits/permit_cap_info/')
        ]);
        
        setRefreshedProfile(profileResponse.data);
        // console.log('Permit cap info received:', permitCapResponse.data);
        setPermitCapInfo(permitCapResponse.data);
      } catch (error) {
        console.error('Failed to refresh data:', error);
        // Fall back to existing profile data
        setRefreshedProfile(profile);
      } finally {
        setPageLoading(false);
      }
    };
    
    refreshProfile();
  }, [user, profile]);

  // Use refreshed profile data if available, otherwise fall back to context profile
  const userData = refreshedProfile || profile;

  if (!userData || pageLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header Section */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            <p className="mt-2 text-lg text-gray-600">
              Welcome<span className="font-medium text-customIndigo">{profile?.first_name ? ", " + profile?.first_name : ""}</span>!
            </p>
          </div>

          {/* Stats Grid with Loading Skeletons */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-8">
            {/* Available Permits Skeleton */}
            <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-2 md:mb-4">
                <h2 className="text-sm font-medium text-gray-600">Available Permits</h2>
                <span className="text-green-600 bg-green-50 p-2 rounded-lg">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </span>
              </div>
              <div className="flex items-baseline mb-3">
                <div className="h-8 bg-gray-200 rounded w-16 animate-pulse"></div>
                <div className="ml-2 h-4 bg-gray-200 rounded w-16 animate-pulse"></div>
              </div>
              <div className="mb-3">
                <div className="flex justify-between items-center mb-1">
                  <div className="h-3 bg-gray-200 rounded w-20 animate-pulse"></div>
                  <div className="h-3 bg-gray-200 rounded w-8 animate-pulse"></div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="h-2 bg-gray-300 rounded-full w-1/3 animate-pulse"></div>
                </div>
              </div>
              <div className="h-3 bg-gray-200 rounded w-40 animate-pulse"></div>
            </div>

            {/* Activated Permits Skeleton */}
            <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-2 md:mb-4">
                <h2 className="text-sm font-medium text-gray-600">Activated Permits</h2>
                <span className="text-blue-600 bg-blue-50 p-2 rounded-lg">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2v12a2 2 0 002 2z" />
                  </svg>
                </span>
              </div>
              <div className="flex items-baseline mb-3">
                <div className="h-8 bg-gray-200 rounded w-16 animate-pulse"></div>
                <div className="ml-2 h-4 bg-gray-200 rounded w-12 animate-pulse"></div>
              </div>
              <div className="mb-3">
                <div className="flex justify-between items-center mb-1">
                  <div className="h-3 bg-gray-200 rounded w-20 animate-pulse"></div>
                  <div className="h-3 bg-gray-200 rounded w-16 animate-pulse"></div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="h-2 bg-gray-300 rounded-full w-1/2 animate-pulse"></div>
                </div>
              </div>
              <div className="h-3 bg-gray-200 rounded w-48 animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totalRegistrations = userData?.active_registrations || 0;
  const maxAllowed = userData?.permit_limit || 0;
  const remaining = maxAllowed - totalRegistrations;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-2 text-lg text-gray-600">
            Welcome<span className="font-medium text-customIndigo">{profile?.first_name ? ", " + profile?.first_name : ""}</span>!
          </p>
          {profile?.email && (
            <p className="mt-1 text-sm text-gray-500">
              {profile.email}
            </p>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-8">
          {/* Available Permits */}
          <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 border border-gray-100 hover:shadow-md transition-all duration-200">
            <div className="flex items-center justify-between mb-2 md:mb-4">
              <h2 className="text-sm font-medium text-gray-600">Available Permits</h2>
              <span className="text-green-600 bg-green-50 p-2 rounded-lg">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </span>
            </div>
            <div className="flex items-baseline mb-3">
              <p className="text-3xl md:text-4xl font-bold text-gray-900">{remaining}</p>
              <span className="ml-2 text-sm text-gray-500">available</span>
            </div>
            <div className="mb-3">
              <div className="flex justify-between items-center mb-1">
                <p className="text-xs sm:text-sm text-gray-500">Active Permits: {totalRegistrations}/{maxAllowed}</p>
                <p className="text-xs sm:text-sm font-medium text-gray-700">
                  {maxAllowed > 0 ? Math.round(((maxAllowed - remaining) / maxAllowed) * 100) : 0}%
                </p>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-500 ${
                    maxAllowed > 0 && ((maxAllowed - remaining) / maxAllowed) * 100 >= 90 
                      ? 'bg-red-500' 
                      : maxAllowed > 0 && ((maxAllowed - remaining) / maxAllowed) * 100 >= 70 
                        ? 'bg-orange-500' 
                        : 'bg-green-500'
                  }`}
                  style={{ width: `${maxAllowed > 0 ? ((maxAllowed - remaining) / maxAllowed) * 100 : 0}%` }}
                ></div>
              </div>
            </div>
            <p className="text-xs text-gray-500">
              {maxAllowed > 0 
                ? 'Permits running at the same time'
                : 'No permit limit'
              }
            </p>
          </div>

          {/* Activated Permits */}
          <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 border border-gray-100 hover:shadow-md transition-all duration-200">
            <div className="flex items-center justify-between mb-2 md:mb-4">
              <h2 className="text-sm font-medium text-gray-600">Activated Permits</h2>
              <span className="text-blue-600 bg-blue-50 p-2 rounded-lg">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2v12a2 2 0 002 2z" />
                </svg>
              </span>
            </div>
            
            <div className="flex items-baseline mb-3">
              <p className="text-3xl md:text-4xl font-bold text-gray-900">{permitCapInfo?.permits_used || 0}</p>
              <span className="ml-2 text-sm text-gray-500">
                {permitCapInfo && permitCapInfo.permit_cap_limit > 0 ? `/ ${permitCapInfo.permit_cap_limit}` : ' activated'}
              </span>
            </div>
            
            <div className="mb-3">
              <div className="flex justify-between items-center mb-1">
                <p className="text-xs sm:text-sm text-gray-500">{permitCapInfo?.period_name || 'This period'}</p>
                {permitCapInfo && permitCapInfo.permit_cap_limit > 0 ? (
                  <p className="text-xs sm:text-sm font-medium text-gray-700">
                    {permitCapInfo.permits_remaining} remaining
                  </p>
                ) : (
                  <p className="text-xs sm:text-sm font-medium text-green-600">Unlimited</p>
                )}
              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                  {permitCapInfo && permitCapInfo.permit_cap_limit > 0 ? (
                    <div 
                      className={`h-2 rounded-full transition-all duration-500 ${
                        (permitCapInfo.permits_used / permitCapInfo.permit_cap_limit) * 100 >= 90 
                          ? 'bg-red-500' 
                          : (permitCapInfo.permits_used / permitCapInfo.permit_cap_limit) * 100 >= 70 
                            ? 'bg-orange-500' 
                            : 'bg-blue-500'
                      }`}
                      style={{ 
                        width: `${Math.min((permitCapInfo.permits_used / permitCapInfo.permit_cap_limit) * 100, 100)}%`,
                        maxWidth: '100%'
                      }}
                    ></div>
                  ) : (
                    <div className="h-2 bg-green-500 rounded-full w-full"></div>
                  )}
                </div>
            </div>
            
            <p className="text-xs text-gray-500">
              {permitCapInfo && permitCapInfo.permit_cap_limit > 0 
                ? `Only counts permits when activated - ${permitCapInfo.period_name}`
                : 'No permit cap on permit activations'
              }
            </p>
          </div>
        </div>

        {/* What This Means Section - Full Width */}
        <div className="bg-white rounded-xl shadow-sm p-6 md:p-8 border border-gray-100 hover:shadow-md transition-all duration-200">
          <div className="flex items-center mb-4">
            <span className="text-customIndigo bg-customIndigo/10 p-3 rounded-lg mr-4">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </span>
            <h2 className="text-xl font-semibold text-gray-900">What This Means</h2>
          </div>
          <div className="space-y-6 text-gray-700">
            <div className="flex items-start">
              <span className="text-customIndigo font-semibold mr-3 text-lg">•</span>
              <div className="flex-1">
                <p className="text-base leading-relaxed">
                  You can have up to <span className="font-semibold text-gray-900">{maxAllowed}</span> permits running at once
                </p>
              </div>
            </div>
            {permitCapInfo && permitCapInfo.permit_cap_limit > 0 ? (
              <div className="flex items-start">
                <span className="text-customIndigo font-semibold mr-3 text-lg">•</span>
                <div className="flex-1">
                  <p className="text-base leading-relaxed">
                    You can activate up to <span className="font-semibold text-gray-900">{permitCapInfo.permit_cap_limit}</span> permits per {permitCapInfo.permit_cap_type === 'quarterly' ? 'quarter' : 'month'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-start">
                <span className="text-customIndigo font-semibold mr-3 text-lg">•</span>
                <div className="flex-1">
                  <p className="text-base leading-relaxed">
                    No permit cap on permit activations
                  </p>
                </div>
              </div>
            )}
            <div className="flex items-start">
              <span className="text-customIndigo font-semibold mr-3 text-lg">•</span>
              <div className="flex-1">
                <p className="text-base leading-relaxed">
                  <span className="font-semibold text-gray-900">Active permits</span><br/>
                  <span className="text-gray-600 text-sm">Currently running and count toward your limit</span>
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-customIndigo font-semibold mr-3 text-lg">•</span>
              <div className="flex-1">
                <p className="text-base leading-relaxed">
                  <span className="font-semibold text-gray-900">Available permits</span><br/>
                  <span className="text-gray-600 text-sm">Show how many more you can activate right now</span>
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-customIndigo font-semibold mr-3 text-lg">•</span>
              <div className="flex-1">
                <p className="text-base leading-relaxed">
                  <span className="font-semibold text-gray-900">Activated permits</span><br/>
                  <span className="text-gray-600 text-sm">Counts permits when activated per {permitCapInfo?.permit_cap_type === 'quarterly' ? 'quarter' : 'month'}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
